import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import CTASection from "../components/CTASection";

const STEPS = [
  {
    n: "01",
    title: "Submit Your Enquiry",
    desc: "Fill in our simple online enquiry form with your basic details and loan requirement. It takes less than two minutes — no documents needed at this stage.",
    tag: "Online Form",
  },
  {
    n: "02",
    title: "Get Assistance",
    desc: "Our team reviews your enquiry and connects with you personally to understand your profile, income details and goals, and explains the way forward in plain language.",
    tag: "Personal Consultation",
  },
  {
    n: "03",
    title: "Explore Suitable Loan Options",
    desc: "Based on your profile, we help you explore and compare suitable loan options offered by relevant banks and financial institutions, including eligibility and documentation requirements.",
    tag: "Option Comparison",
  },
  {
    n: "04",
    title: "Apply Through the Institution",
    desc: "Once you choose an option, we support your application and coordinate with the bank or NBFC. You complete the application directly with the institution, and approval is subject to their eligibility criteria and policies.",
    tag: "Institution Application",
  },
];

export default function HowItWorks() {
  return (
    <main>
      <PageHero
        testid="how-it-works-hero"
        eyebrow="How It Works"
        titleLines={["Four Simple Steps to", "the Right Loan Option"]}
        description="A clear, guided journey — from your first enquiry to a complete application with the relevant bank or financial institution."
      />

      <section className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pb-20 lg:pb-28" data-testid="how-it-works-steps">
        <div className="relative">
          <div className="absolute left-[27px] sm:left-[31px] top-4 bottom-4 w-px bg-gradient-to-b from-[rgba(212,175,55,0.5)] via-[rgba(212,175,55,0.2)] to-transparent" />
          <div className="space-y-12">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <div className="flex gap-6 sm:gap-8 items-start" data-testid={`how-step-${i + 1}`}>
                  <div className="relative shrink-0">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border border-[rgba(212,175,55,0.4)] bg-[#121216] flex items-center justify-center">
                      <span className="font-display text-lg sm:text-xl font-bold gold-text">{s.n}</span>
                    </div>
                  </div>
                  <div className="card-gold flex-1 p-6 sm:p-8 hover:!transform-none">
                    <p className="font-mono-brand text-[10px] text-[#D4AF37] tracking-[0.28em] uppercase mb-3">{s.tag}</p>
                    <h2 className="font-display text-xl sm:text-2xl font-semibold text-white mb-3">{s.title}</h2>
                    <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1} className="mt-14">
          <div className="flex items-start gap-4 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#121216] p-6" data-testid="how-it-works-note">
            <Info size={20} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#A1A1AA] leading-relaxed">
              CREDNOVA SERVICES PRIVATE LIMITED facilitates and assists with the process. The final application,
              credit assessment and approval decision rest entirely with the respective bank or financial institution.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-10 text-center">
          <Link to="/contact" data-testid="how-it-works-cta" className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm">
            <span className="relative z-10 flex items-center gap-2">
              Start With Step One <ArrowRight size={16} />
            </span>
          </Link>
        </Reveal>
      </section>

      <CTASection
        title="One Enquiry Is All It Takes"
        subtitle="Submit your details today and let our team guide you towards suitable loan options."
      />
    </main>
  );
}
